import { Accordion } from "@mantine/core";
import { IconBuildingWarehouse, IconHome, IconSteeringWheel, IconTruck } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import RouteMap, { type RouteStop } from "#/components/ui/map/RouteMap";
import { useClient } from "#/hook/useClient";
import { usePrvTranslation } from "#/pages/ClientDetail/components/prv/usePrvTranslation";
import type {
  PredictionBoard,
  ShipmentCard,
  ShipmentTripPlan,
  TripLeg,
  TripReason,
} from "#/types/prediction";
import { formatDate, formatWeekday, weekdayShortName } from "#/utils/dateTimeUtils";
import { withBoldValues } from "#/pages/ClientDetail/components/prv/prvText";
import { formatMeasure, formatMinutes, percentOf, shipmentWhy } from "../predictionUtils";
import StopLoad from "./StopLoad";

interface Props {
  card: ShipmentCard;
  board: Pick<PredictionBoard, "categories">;
  /** TMA admins only: show the reasons box (why this Site, these trucks, these days). */
  showReasons: boolean;
}

/** Stops are green, the way back blue, on the timeline and on the map alike (Ammad, 2026-09-30). */
const STOP_COLOR = "#39ff14";
/** Dark edge under the route line, as Google and Apple draw theirs: bright fill, darker outline. */
const STOP_CASING = "#064e3b";
const RETURN_COLOR = "#2563eb";

/** Text colour of the trip's status line in the Load box. */
const REASON_TEXT: Record<TripReason, string> = {
  fits: "text-green-700",
  exceeds_truck: "text-orange-700",
  no_driver: "text-red-700",
  no_day_found: "text-red-700",
  exceeds_day: "text-orange-700",
};

const Fact = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <span className="text-xs text-gray-500">{label}</span>
    <span className="text-sm font-semibold text-gray-900 tabular-nums">{children}</span>
  </div>
);

/**
 * The itinerary: one entry per trip (truck with its limits, day, driver,
 * times, what is on board), a plain "why" line when the trip needs one, and
 * inside it the stops of that trip in driving order, each with the items to
 * drop there. Both levels are timelines with numbered circles.
 */
const ShipmentTripsCard = ({ card, board, showReasons }: Props) => {
  const t = usePrvTranslation();
  const { i18n } = useTranslation();
  const { client } = useClient();
  const navigate = useNavigate();
  const plan = card.plan!;
  const stopByOrder = (order: number) => plan.stops.find((stop) => stop.order === order);
  const why = showReasons ? shipmentWhy(plan, card.shipment.shipmentDate, t, i18n.language) : [];
  const mapOrigin: RouteStop | null = plan.origin
    ? {
        label: "",
        title: plan.origin.name,
        latitude: plan.origin.latitude,
        longitude: plan.origin.longitude,
      }
    : null;

  /** The stops in driving order with their items (left) and the trip's road on a map (right). */
  interface StopsView {
    stopOrders: number[];
    legs: TripLeg[];
    offloadingPerStop: number;
    departAt: string | null;
    /** Null when no truck drives: the route is shown, no way back. */
    returnLeg: { minutes: number | null; km: number } | null;
    returnAt: string | null;
  }
  const stopsAndMap = (view: StopsView) => (
    <>
      {/* The stops of this trip, in driving order, each with what to drop there (left),
          and this trip's own road on a map, depot → stops in blue, way back dashed grey (right). */}
      <div className="grid grid-cols-1 gap-4 border-t border-indigo-200 pt-3 lg:grid-cols-[3fr_2fr]">
        <div className="flex min-w-0 flex-col gap-3">
          <span className="text-xs font-bold tracking-wider text-indigo-700 uppercase">
            {t("predictedDeliveries.shipment.trips.stops")}
          </span>
          <ol className="relative flex flex-col gap-4 pl-10">
            <div className="absolute top-3 bottom-3 left-[15px] w-0 border-l-2 border-dashed border-green-300" />
            {/* Where the trip starts: the Site, as the old route list showed it. */}
            {plan.origin && (
              <li className="relative flex flex-col gap-1">
                <span className="absolute top-0 -left-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-gray-800 text-white shadow">
                  <IconBuildingWarehouse size={16} />
                </span>
                <span className="flex min-h-8 items-center text-sm font-semibold text-gray-900">
                  {plan.origin.name}
                </span>
                <span className="text-xs text-gray-600 tabular-nums">
                  {plan.origin.address}
                  {view.departAt &&
                    ` · ${t("predictedDeliveries.shipment.trips.departAt", { time: view.departAt })}`}
                </span>
              </li>
            )}
            {view.stopOrders.map((order, position) => {
              const stop = stopByOrder(order);
              if (!stop) return null;
              const leg = view.legs[position];
              return (
                <li key={order} className="relative flex flex-col gap-2">
                  <span className="flex min-h-8 items-center gap-2 text-sm font-semibold text-gray-900">
                    <span className="absolute top-1 -left-[37px] flex h-6 w-6 items-center justify-center rounded-full border-2 border-green-600 bg-white text-xs font-bold text-green-700 tabular-nums shadow">
                      {stop.order}
                    </span>
                    <button
                      type="button"
                      className="text-left text-green-700 underline-offset-2 hover:underline"
                      onClick={() => void navigate(`/sub-clients/${stop.subClientId}`)}
                    >
                      {stop.name}
                    </button>
                    <span className="text-xs font-normal text-gray-500 tabular-nums">
                      {t("predictedDeliveries.shipment.route.units", { count: stop.units })}
                    </span>
                  </span>
                  {leg && (
                    <span className="text-xs text-gray-600 tabular-nums">
                      {leg.arriveAt && leg.leaveAt
                        ? t("predictedDeliveries.shipment.trips.legTimes", {
                            arrive: leg.arriveAt,
                            minutes: formatMinutes(leg.minutes),
                            km: leg.km,
                            offloading: formatMinutes(view.offloadingPerStop),
                            leave: leg.leaveAt,
                          })
                        : leg.minutes !== null
                          ? t("predictedDeliveries.shipment.trips.legNoClock", {
                              minutes: formatMinutes(leg.minutes),
                              km: leg.km,
                            })
                          : t("predictedDeliveries.shipment.route.legNoTime", { km: leg.km })}
                    </span>
                  )}
                  <StopLoad stop={stop} card={card} board={board} />
                </li>
              );
            })}
            {/* The way back closes the timeline: same circle, a home icon instead of a number. */}
            {view.returnLeg && (
              <li className="relative flex flex-col gap-1">
                <span className="flex min-h-8 items-center gap-2 text-sm font-semibold text-gray-900">
                  <span className="absolute top-1 -left-[37px] flex h-6 w-6 items-center justify-center rounded-full border-2 border-blue-500 bg-white text-blue-700 shadow">
                    <IconHome size={13} />
                  </span>
                  <span className="text-blue-700">
                    {t("predictedDeliveries.shipment.trips.returnTitle")}
                  </span>
                </span>
                <span className="text-xs text-gray-600 tabular-nums">
                  {t("predictedDeliveries.shipment.trips.returnLeg", {
                    minutes: formatMinutes(view.returnLeg.minutes),
                    km: view.returnLeg.km,
                    back: view.returnAt ?? "--",
                  })}
                </span>
              </li>
            )}
          </ol>
        </div>
        <RouteMap
          origin={mapOrigin}
          stops={view.stopOrders.flatMap((order) => {
            const stop = stopByOrder(order);
            return stop
              ? [
                  {
                    label: String(stop.order),
                    title: stop.name,
                    latitude: stop.latitude,
                    longitude: stop.longitude,
                  },
                ]
              : [];
          })}
          showReturn={view.returnLeg !== null}
          originMarker="warehouse"
          muted
          routeColor={STOP_COLOR}
          casingColor={STOP_CASING}
          returnColor={RETURN_COLOR}
          className="h-72 lg:h-auto lg:min-h-[20rem]"
        />
      </div>
    </>
  );

  const tripCard = (trip: ShipmentTripPlan) => {
    return (
      <Accordion.Item key={trip.index} value={String(trip.index)}>
        <Accordion.Control>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="text-base font-black text-indigo-800 uppercase">
                {t("predictedDeliveries.shipment.trips.trip", { index: trip.index })}
              </span>
              <span className="text-sm font-semibold text-gray-900 capitalize tabular-nums">
                {formatWeekday(trip.day, i18n.language)} {formatDate(trip.day, client)}
                {trip.dayOffset > 0 && (
                  <span className="ml-2 text-xs font-normal text-orange-700 normal-case">
                    {t("predictedDeliveries.shipment.trips.laterDay", { count: trip.dayOffset })}
                  </span>
                )}
              </span>
              <span className="text-xs font-medium text-gray-800 tabular-nums">
                {t("predictedDeliveries.shipment.trips.headerSummary", {
                  stops: trip.stopOrders.length,
                  km: trip.distanceKm,
                })}
              </span>
            </div>
          </div>
        </Accordion.Control>

        <Accordion.Panel>
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-4">
              <Fact label={t("predictedDeliveries.shipment.trips.truck")}>
                <span className="flex flex-col">
                  <button
                    type="button"
                    className="flex items-center gap-1.5 text-left underline-offset-2 hover:underline"
                    onClick={() => void navigate(`/fleet/vehicles/${trip.truck.vehicleId}`)}
                  >
                    <IconTruck size={14} className="text-gray-400" />
                    {trip.truck.name}
                  </button>
                  <span className="text-xs font-normal text-gray-500">
                    {trip.truck.modelName} ·{" "}
                    {t("predictedDeliveries.shipment.trips.truckLimits", {
                      kg: formatMeasure(trip.truck.payloadKg),
                      m3: formatMeasure(trip.truck.capacityM3),
                    })}
                  </span>
                </span>
              </Fact>
              <Fact label={t("predictedDeliveries.shipment.trips.driver")}>
                {trip.driver ? (
                  <span className="flex flex-col">
                    <span className="flex items-center gap-1">
                      <IconSteeringWheel size={14} className="text-gray-400" />
                      {trip.driver.name}
                    </span>
                    <span className="text-xs font-normal text-gray-500">
                      {trip.driver.startTime}–{trip.driver.endTime} ·{" "}
                      {trip.driver.workingDays
                        .map((weekday) => weekdayShortName(weekday, i18n.language))
                        .join(" ")}
                    </span>
                  </span>
                ) : (
                  <span className="text-red-700">
                    {t("predictedDeliveries.shipment.trips.noDriver")}
                  </span>
                )}
              </Fact>
              <Fact label={t("predictedDeliveries.shipment.trips.times")}>
                <span className="flex flex-col">
                  <span>
                    {trip.departAt && trip.returnAt
                      ? t("predictedDeliveries.shipment.trips.departReturn", {
                          depart: trip.departAt,
                          return: trip.returnAt,
                        })
                      : "--"}
                  </span>
                  <span className="text-xs font-normal text-gray-500">
                    {t("predictedDeliveries.shipment.trips.breakdown", {
                      driving: formatMinutes(trip.drivingMinutes),
                      offloading: formatMinutes(trip.offloadingMinutes),
                      km: trip.distanceKm,
                    })}
                  </span>
                </span>
              </Fact>
              {/* What this trip carries against the whole shipment and the truck's limits; one line, wraps when long. */}
              <div
                className={`flex min-w-0 flex-col gap-0.5 ${trip.fits === false ? "text-orange-700" : "text-gray-700"}`}
              >
                <span className="text-xs text-gray-500">
                  {t("predictedDeliveries.shipment.trips.load")}
                </span>
                <span className="text-sm tabular-nums">
                  {withBoldValues(t, "predictedDeliveries.shipment.trips.loadLine", {
                    count: trip.units,
                    total: plan.total.units,
                    kg: formatMeasure(trip.kg),
                    totalKg: `${formatMeasure(plan.total.kg)} kg`,
                    kgPercent: percentOf(trip.kg, trip.truck.payloadKg),
                    m3: formatMeasure(trip.m3),
                    totalM3: `${formatMeasure(plan.total.m3)} m³`,
                    m3Percent: percentOf(trip.m3, trip.truck.capacityM3),
                  })}
                </span>
                <span className={`text-sm font-bold ${REASON_TEXT[trip.reason]}`}>
                  {t(`predictedDeliveries.shipment.trips.reason.${trip.reason}`, {
                    runs: trip.runs,
                  })}
                </span>
              </div>
            </div>

            {stopsAndMap({
              stopOrders: trip.stopOrders,
              legs: trip.legs,
              offloadingPerStop: trip.offloadingMinutes / Math.max(trip.legs.length, 1),
              departAt: trip.departAt,
              returnLeg: trip.returnLeg,
              returnAt: trip.returnAt,
            })}
          </div>
        </Accordion.Panel>
      </Accordion.Item>
    );
  };

  /**
   * No trip could be planned (no truck at the Site, data missing, no Site):
   * one block in the same shape, the cause in red where the truck would be,
   * and everything that does exist: the stops, their items, the road.
   */
  const unplannedCard = () => {
    const cause: "no_site" | "missing_data" | "no_truck" = !plan.origin
      ? "no_site"
      : plan.warnings.includes("missing_data")
        ? "missing_data"
        : "no_truck";
    const legs: TripLeg[] = plan.stops.map((stop) => ({
      order: stop.order,
      minutes: stop.legFromPrevious?.minutes ?? null,
      km: stop.legFromPrevious?.km ?? 0,
      arriveAt: null,
      leaveAt: null,
    }));
    const km = Math.round(legs.reduce((sum, leg) => sum + leg.km, 0) * 10) / 10;
    return (
      <Accordion.Item key="unplanned" value="unplanned">
        <Accordion.Control>
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-base font-black text-red-700 uppercase">
              {t(`predictedDeliveries.shipment.trips.problem.${cause}`)}
            </span>
            <span className="text-sm font-semibold text-gray-900 capitalize tabular-nums">
              {formatWeekday(card.shipment.shipmentDate, i18n.language)}{" "}
              {formatDate(card.shipment.shipmentDate, client)}
            </span>
            <span className="text-xs font-medium text-gray-800 tabular-nums">
              {t("predictedDeliveries.shipment.trips.headerSummary", {
                stops: plan.stops.length,
                km,
              })}
            </span>
          </div>
        </Accordion.Control>
        <Accordion.Panel>
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-4">
              <Fact label={t("predictedDeliveries.shipment.trips.truck")}>
                <span className="text-red-700">
                  {t(`predictedDeliveries.shipment.trips.problemDetail.${cause}`, {
                    site: plan.origin?.name ?? "",
                  })}
                </span>
              </Fact>
              <Fact label={t("predictedDeliveries.shipment.trips.driver")}>
                <span className="text-red-700">--</span>
              </Fact>
              <Fact label={t("predictedDeliveries.shipment.trips.times")}>
                <span className="text-red-700">--</span>
              </Fact>
              <div className="flex min-w-0 flex-col gap-0.5 text-gray-700">
                <span className="text-xs text-gray-500">
                  {t("predictedDeliveries.shipment.trips.load")}
                </span>
                <span className="text-sm tabular-nums">
                  {withBoldValues(t, "predictedDeliveries.shipment.trips.loadTotal", {
                    count: plan.total.units,
                    kg: `${formatMeasure(plan.total.kg)} kg`,
                    m3: `${formatMeasure(plan.total.m3)} m³`,
                  })}
                </span>
                <span className="text-sm font-bold text-red-700">
                  {t("predictedDeliveries.shipment.trips.notPlanned")}
                </span>
              </div>
            </div>
            {stopsAndMap({
              stopOrders: plan.stops.map((stop) => stop.order),
              legs,
              offloadingPerStop: 0,
              departAt: null,
              returnLeg: null,
              returnAt: null,
            })}
          </div>
        </Accordion.Panel>
      </Accordion.Item>
    );
  };

  return (
    <section className="flex flex-col gap-3">
      {/* One "why" for the whole shipment, only when its trips need explaining. */}
      {/* The reasons behind the trips, one per line. TMA admins only; empty for a client user. */}
      {why.length > 0 && (
        <ul className="flex flex-col gap-1.5 rounded-md border-l-4 border-amber-500 bg-amber-100/70 px-4 py-3 text-sm font-medium text-amber-950">
          {why.map((line, index) => (
            <li key={index}>
              {line.text}
              {line.children && line.children.length > 0 && (
                <ul className="mt-1 flex list-disc flex-col gap-1 pl-5 font-normal">
                  {line.children.map((child, childIndex) => (
                    <li key={childIndex}>{child}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
      <Accordion
        multiple
        variant="separated"
        defaultValue={[plan.trips.length > 0 ? String(plan.trips[0].index) : "unplanned"]}
        classNames={{
          // Plain white, overriding the theme's client-colour tint on accordion headers.
          item: "!border !border-indigo-200 !bg-white rounded-lg shadow-sm",
          control: "!bg-indigo-50 hover:!bg-indigo-100/70 rounded-t-lg",
          content: "pt-3",
        }}
      >
        {plan.trips.length > 0 ? plan.trips.map(tripCard) : unplannedCard()}
      </Accordion>
    </section>
  );
};

export default ShipmentTripsCard;
